/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import PropTypes, { string } from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import { Grid, Link, Pagination, Typography, withTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import { CMLTextSnippet } from 'bundles/cml';
import { ForumThreadCard } from 'bundles/discussions/components/personalization-ForumThreadCard';
import YourActivityTabDataProvider from 'bundles/discussions/components/personalization/__components/__providers/YourActivityTabDataProvider';
import { defaultPageSize } from 'bundles/discussions/constants';
import { handlePaginationAriaLabel } from 'bundles/discussions/utils/handlePaginationAriaLabel';
import { TrackedReactLink } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/discussions';

import type { ForumPostSearchResult } from '../__providers/__types__';
import getThreadUrl from '../__providers/forumsSearchAPIUtilities/getThreadURL';

const styles = (theme: Theme) =>
  css({
    listStyle: 'none',
    padding: `var(--cds-spacing-300) 0`,
    position: 'relative',
    borderBottom: `1px solid var(--cds-color-grey-50)`,
    '&:last-child': {
      borderBottom: 'none',
    },
    '&.focused, &:hover, &.focused a': {
      textDecoration: 'none',
    },
    '.forReader': {
      height: 0,
      width: 0,
    },
  });

const renderPost = (post: ForumPostSearchResult, theme: Theme, userId: string) => {
  const { forumPost, creatorProfile, lastRepliedUserProfile } = post;
  const threadUrl = getThreadUrl(post);
  const TopLevelThreadCreator = {
    ...creatorProfile,
    userId: Number(creatorProfile?.userId),
    id: creatorProfile?.userId,
    learnerId: Number(creatorProfile?.userId),
  };
  const lastReplier = lastRepliedUserProfile
    ? {
        ...lastRepliedUserProfile,
        userId: Number(lastRepliedUserProfile.userId),
        id: lastRepliedUserProfile?.userId,
        learnerId: Number(lastRepliedUserProfile?.userId),
      }
    : undefined;

  return (
    <li css={styles(theme)} aria-label={_t('Post: #{post}', { post: forumPost.title })}>
      <ForumThreadCard
        answerCount={parseInt(forumPost.topLevelReplyCount, 10)}
        badge={forumPost.badges?.[0]}
        createdAt={forumPost.createdAtTime}
        creator={TopLevelThreadCreator}
        description={<CMLTextSnippet cml={{ typeName: 'cml', definition: forumPost.content.cmlContent }} />}
        forumName={post.forumInfo.forumTitle}
        forumQuestionId={forumPost.topLevelPostId}
        isUpvoted={forumPost.isUpvoted}
        myPostOrReplyBadgeText={creatorProfile?.userId === userId ? 'MY_POST' : 'REPLIED'}
        lastContributor={lastReplier || TopLevelThreadCreator}
        lastReplyDate={forumPost.lastRepliedAtTime}
        pinned={forumPost.isPinned}
        replyCount={parseInt(forumPost.topLevelReplyCount, 10)}
        threadUrl={threadUrl}
        title={forumPost.title}
        titleLink={
          (threadUrl && (
            <Typography variant="h2semibold" className="rc-personalization_ForumThreadCard__title">
              <Link
                component={TrackedReactLink}
                href={threadUrl}
                trackingName="YourActivityTab_PostLink"
                data={{
                  question_id: forumPost.topLevelPostId,
                  question_user_id: forumPost.creatorUserId,
                }}
              >
                {forumPost.title}
              </Link>
            </Typography>
          )) ||
          undefined
        }
        upvoteCount={parseInt(forumPost.upvoteCount, 10)}
      />
    </li>
  );
};

class YourActivityTabBody extends React.Component<{ theme: Theme; hasBeenVisited?: boolean }> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
    courseId: string.isRequired,
    userId: string.isRequired,
  };

  declare context: LegacyContextType<typeof YourActivityTabBody.contextTypes>;

  onPageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    const router = this.context.router;
    router.push({
      pathname: router.location.pathname,
      params: {},
      query: Object.assign(router.location.query, { page }),
    });
  };

  render() {
    const { router, courseId, userId } = this.context;
    const { theme, hasBeenVisited } = this.props;
    const currentPage = router.location.query.page ? parseInt(router.location.query.page, 10) : 1;
    return (
      <div className="rc-personalization_YourActivityTabBody">
        <YourActivityTabDataProvider
          courseId={courseId}
          userId={userId}
          currentPage={currentPage}
          hasBeenVisited={hasBeenVisited}
        >
          {({ loading, error, data }) => {
            if (error) return null;
            if (loading) {
              return (
                <Typography
                  variant="h2semibold"
                  css={css`
                    margin-top: var(--cds-spacing-300);
                  `}
                >
                  {_t('Loading...')}
                </Typography>
              );
            }
            if (data) {
              if (data.postResults.length === 0) {
                return (
                  <Typography
                    variant="h2semibold"
                    css={css`
                      margin-top: var(--cds-spacing-300);
                    `}
                  >
                    {_t('No posts yet')}
                  </Typography>
                );
              }

              const total = parseInt(data.totalHits, 10);
              const pageCount = Math.ceil(total / defaultPageSize);
              const showPaginationControls = pageCount > 1;

              return (
                <React.Fragment>
                  <ul>
                    {data.postResults.map((post) => (
                      <React.Fragment key={post.forumPost.postId}>{renderPost(post, theme, userId)}</React.Fragment>
                    ))}
                  </ul>

                  {showPaginationControls && (
                    <Grid
                      container
                      item
                      xs={12}
                      css={{ justifyContent: 'center', marginTop: 'var(--cds-spacing-400)' }}
                    >
                      <Pagination
                        getItemAriaLabel={handlePaginationAriaLabel}
                        total={total}
                        page={currentPage}
                        defaultPageSize={defaultPageSize}
                        onPageChange={this.onPageChange}
                        hidePageSizeInput
                      />
                    </Grid>
                  )}
                </React.Fragment>
              );
            }
            return null;
          }}
        </YourActivityTabDataProvider>
      </div>
    );
  }
}

export default withTheme(YourActivityTabBody);
